import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, RefreshCw, Loader } from 'lucide-react';
import { adminCustomers } from '../../services/api';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const generatePassword = () => {
  return Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 8).toUpperCase();
};

const initialForm = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  abcLicenseNumber: '',
  resaleNumber: '',
  // Shipping address
  shippingAddressLine: '',
  shippingCity: '',
  shippingState: '',
  shippingZip: '',
  // Billing address
  billingSameAsShipping: true,
  billingAddressLine: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  paymentType: 'Invoice',
  netDays: 30,
  creditCap: 0,
  assignedRepID: '',
  isApproved: true,
};

export default function CustomerCreate() {
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [reps, setReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [clipboardMsg, setClipboardMsg] = useState('');

  useEffect(() => {
    const fetchReps = async () => {
      try {
        const res = await adminCustomers.reps();
        if (res.data && res.data.success) {
          setReps(res.data.data || []);
        }
      } catch (err) {
        // non-fatal — rep list just stays empty
      } finally {
        setRepsLoading(false);
      }
    };
    fetchReps();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleGeneratePassword = async () => {
    const pwd = generatePassword();
    setForm((prev) => ({ ...prev, password: pwd }));
    try {
      await navigator.clipboard.writeText(pwd);
      setClipboardMsg('Copied to clipboard!');
      setTimeout(() => setClipboardMsg(''), 2500);
    } catch {
      setClipboardMsg('Password generated (clipboard unavailable).');
      setTimeout(() => setClipboardMsg(''), 2500);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.companyName.trim()) newErrors.companyName = 'Company name is required.';
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required.';
    if (!form.email.trim()) newErrors.email = 'Email is required.';
    if (!form.abcLicenseNumber.trim()) newErrors.abcLicenseNumber = 'ABC License # is required.';
    if (!form.resaleNumber.trim()) newErrors.resaleNumber = 'Resale # is required.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        companyName: form.companyName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        abcLicenseNumber: form.abcLicenseNumber.trim(),
        resaleNumber: form.resaleNumber.trim(),
        shippingAddressLine: form.shippingAddressLine.trim(),
        shippingCity: form.shippingCity.trim(),
        shippingState: form.shippingState,
        shippingZip: form.shippingZip.trim(),
        billingSameAsShipping: form.billingSameAsShipping,
        billingAddressLine: form.billingSameAsShipping ? '' : form.billingAddressLine.trim(),
        billingCity: form.billingSameAsShipping ? '' : form.billingCity.trim(),
        billingState: form.billingSameAsShipping ? '' : form.billingState,
        billingZip: form.billingSameAsShipping ? '' : form.billingZip.trim(),
        paymentType: form.paymentType,
        netDays: Number(form.netDays),
        creditCap: Number(form.creditCap),
        assignedRepID: form.assignedRepID || null,
        isApproved: form.isApproved,
      };

      const res = await adminCustomers.create(payload);
      if (res.data && res.data.success) {
        const { customerID } = res.data.data;
        navigate(`/admin/customers/${customerID}`);
      } else {
        setApiError((res.data && res.data.message) || 'An unexpected error occurred.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'An unexpected error occurred.';
      setApiError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="d-flex align-items-center mb-4">
        <UserPlus size={22} className="me-2 text-primary" />
        <h4 className="mb-0">Create New Customer</h4>
      </div>

      {apiError && (
        <div className="alert alert-danger" role="alert">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>

        {/* Company Information */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <strong>Company Information</strong>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="companyName" className="form-label">
                Company Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                className={`form-control${errors.companyName ? ' is-invalid' : ''}`}
                value={form.companyName}
                onChange={handleChange}
                placeholder="Acme Distributors LLC"
              />
              {errors.companyName && (
                <div className="invalid-feedback">{errors.companyName}</div>
              )}
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="abcLicenseNumber" className="form-label">
                  ABC License # <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="abcLicenseNumber"
                  name="abcLicenseNumber"
                  className={`form-control${errors.abcLicenseNumber ? ' is-invalid' : ''}`}
                  value={form.abcLicenseNumber}
                  onChange={handleChange}
                />
                {errors.abcLicenseNumber && (
                  <div className="invalid-feedback">{errors.abcLicenseNumber}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="resaleNumber" className="form-label">
                  Resale # <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="resaleNumber"
                  name="resaleNumber"
                  className={`form-control${errors.resaleNumber ? ' is-invalid' : ''}`}
                  value={form.resaleNumber}
                  onChange={handleChange}
                />
                {errors.resaleNumber && (
                  <div className="invalid-feedback">{errors.resaleNumber}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <strong>Contact Information</strong>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className={`form-control${errors.firstName ? ' is-invalid' : ''}`}
                  value={form.firstName}
                  onChange={handleChange}
                />
                {errors.firstName && (
                  <div className="invalid-feedback">{errors.firstName}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className={`form-control${errors.lastName ? ' is-invalid' : ''}`}
                  value={form.lastName}
                  onChange={handleChange}
                />
                {errors.lastName && (
                  <div className="invalid-feedback">{errors.lastName}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control${errors.email ? ' is-invalid' : ''}`}
                  value={form.email}
                  onChange={handleChange}
                  placeholder="buyer@example.com"
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="phone" className="form-label">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="form-control"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(555) 000-0000"
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="input-group">
                <input
                  type="text"
                  id="password"
                  name="password"
                  className="form-control"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Set or generate a password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleGeneratePassword}
                  title="Generate random password and copy to clipboard"
                >
                  <RefreshCw size={15} className="me-1" />
                  Generate
                </button>
              </div>
              {clipboardMsg && (
                <div className="form-text text-success">{clipboardMsg}</div>
              )}
              <div className="form-text">
                Password is shown in plain text so you can share it with the customer.
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <strong>Shipping Address</strong>
            <span className="text-muted fw-normal small ms-2">Where orders are delivered</span>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="shippingAddressLine" className="form-label">Address</label>
              <input
                type="text"
                id="shippingAddressLine"
                name="shippingAddressLine"
                className="form-control"
                value={form.shippingAddressLine}
                onChange={handleChange}
                placeholder="123 Main St"
              />
            </div>
            <div className="row">
              <div className="col-md-5 mb-3">
                <label htmlFor="shippingCity" className="form-label">City</label>
                <input
                  type="text"
                  id="shippingCity"
                  name="shippingCity"
                  className="form-control"
                  value={form.shippingCity}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label htmlFor="shippingState" className="form-label">State</label>
                <select
                  id="shippingState"
                  name="shippingState"
                  className="form-select"
                  value={form.shippingState}
                  onChange={handleChange}
                >
                  <option value="">— Select —</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="shippingZip" className="form-label">Zip</label>
                <input
                  type="text"
                  id="shippingZip"
                  name="shippingZip"
                  className="form-control"
                  value={form.shippingZip}
                  onChange={handleChange}
                  placeholder="90210"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="card mb-4">
          <div className="card-header bg-light d-flex align-items-center justify-content-between">
            <div>
              <strong>Billing Address</strong>
              <span className="text-muted fw-normal small ms-2">For invoices and statements</span>
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="billingSameAsShipping"
                name="billingSameAsShipping"
                checked={form.billingSameAsShipping}
                onChange={handleChange}
              />
              <label className="form-check-label small" htmlFor="billingSameAsShipping">
                Same as shipping
              </label>
            </div>
          </div>
          <div className="card-body">
            {form.billingSameAsShipping ? (
              <p className="text-muted small mb-0">
                Billing address will match the shipping address above.
              </p>
            ) : (
              <>
                <div className="mb-3">
                  <label htmlFor="billingAddressLine" className="form-label">Address</label>
                  <input
                    type="text"
                    id="billingAddressLine"
                    name="billingAddressLine"
                    className="form-control"
                    value={form.billingAddressLine}
                    onChange={handleChange}
                    placeholder="456 Billing Blvd"
                  />
                </div>
                <div className="row">
                  <div className="col-md-5 mb-3">
                    <label htmlFor="billingCity" className="form-label">City</label>
                    <input
                      type="text"
                      id="billingCity"
                      name="billingCity"
                      className="form-control"
                      value={form.billingCity}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="billingState" className="form-label">State</label>
                    <select
                      id="billingState"
                      name="billingState"
                      className="form-select"
                      value={form.billingState}
                      onChange={handleChange}
                    >
                      <option value="">— Select —</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="billingZip" className="form-label">Zip</label>
                    <input
                      type="text"
                      id="billingZip"
                      name="billingZip"
                      className="form-control"
                      value={form.billingZip}
                      onChange={handleChange}
                      placeholder="90210"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Account Settings */}
        <div className="card mb-4">
          <div className="card-header bg-light">
            <strong>Account Settings</strong>
          </div>
          <div className="card-body">

            {/* Payment Type */}
            <div className="mb-3">
              <label className="form-label d-block">Payment Type</label>
              {['Invoice', 'Credit Card', 'Both'].map((pt) => (
                <div className="form-check form-check-inline" key={pt}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="paymentType"
                    id={`paymentType_${pt}`}
                    value={pt}
                    checked={form.paymentType === pt}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor={`paymentType_${pt}`}>
                    {pt}
                  </label>
                </div>
              ))}
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label htmlFor="netDays" className="form-label">Net Days</label>
                <input
                  type="number"
                  id="netDays"
                  name="netDays"
                  className="form-control"
                  value={form.netDays}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="creditCap" className="form-label">
                  Credit Cap <span className="text-muted small">(0 = no cap)</span>
                </label>
                <input
                  type="number"
                  id="creditCap"
                  name="creditCap"
                  className="form-control"
                  value={form.creditCap}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="assignedRepID" className="form-label">Assign Rep</label>
                <select
                  id="assignedRepID"
                  name="assignedRepID"
                  className="form-select"
                  value={form.assignedRepID}
                  onChange={handleChange}
                  disabled={repsLoading}
                >
                  <option value="">— None —</option>
                  {reps.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.firstName} {rep.lastName}
                    </option>
                  ))}
                </select>
                {repsLoading && (
                  <div className="form-text text-muted">Loading reps...</div>
                )}
              </div>
            </div>

            {/* Auto-approve toggle */}
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isApproved"
                  name="isApproved"
                  checked={form.isApproved}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="isApproved">
                  Auto-approve account
                </label>
              </div>
              <div className="form-text">
                When enabled, the customer will be marked as approved immediately upon creation.
              </div>
            </div>

          </div>
        </div>

        {/* Actions */}
        <div className="d-flex align-items-center gap-3">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader size={15} className="me-2 spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus size={15} className="me-2" />
                Create Customer
              </>
            )}
          </button>
          <Link to="/admin/customers" className="btn btn-link text-secondary ps-0">
            Cancel
          </Link>
        </div>

      </form>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
